import { type AppleSignInCredential, random, type SignInResult, signInClient, signInErrorMessage } from "@serophin/shared";
import { type MutableRef, useEffect, useRef, useState } from "preact/hooks";
import { Spinner } from "../components/Spinner";
import { AppleIcon } from "../icons";
import { type AppleIdAuth, type AppleIdSignInResponse, loadAppleIdAuth } from "./scripts";

export type AppleSignInButtonProps = {
  servicesId: string;
  redirectUri: string;
  onSignedIn: (result: SignInResult) => void | Promise<void>;
  onError?: (message: string) => void;
  baseUrl?: string;
  class?: string;
};

type AppleSignInHandlers = Pick<AppleSignInButtonProps, "onSignedIn" | "onError" | "baseUrl">;

const APPLE_CANCEL_ERRORS = new Set(["popup_closed_by_user", "user_cancelled_authorize"]);

const DEFAULT_APPLE_BUTTON_CLASS =
  "flex w-full items-center justify-center gap-3 rounded-lg border border-transparent bg-black px-4 py-3 font-medium text-white transition hover:bg-neutral-900 disabled:cursor-not-allowed disabled:opacity-50";

const isAppleCancel = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("error" in error)) {
    return false;
  }
  const code = (error as { error: unknown }).error;
  return typeof code === "string" && APPLE_CANCEL_ERRORS.has(code);
};

const toCredential = ({ authorization, user }: AppleIdSignInResponse): AppleSignInCredential => {
  const name = user?.name;
  return {
    identityToken: authorization.id_token,
    authorizationCode: authorization.code,
    fullName: name && (name.firstName || name.lastName) ? { givenName: name.firstName, familyName: name.lastName } : undefined,
  };
};

const useAppleIdAuth = (servicesId: string, redirectUri: string, handlers: MutableRef<AppleSignInHandlers>) => {
  const [auth, setAuth] = useState<AppleIdAuth | null>(null);

  useEffect(() => {
    let active = true;
    setAuth(null);
    loadAppleIdAuth()
      .then((appleAuth) => {
        if (!active) {
          return;
        }
        appleAuth.init({ clientId: servicesId, scope: "name email", redirectURI: redirectUri, usePopup: true, state: random.token(16) });
        setAuth(appleAuth);
      })
      .catch(() => {
        if (active) {
          handlers.current.onError?.("Could not load Apple sign-in.");
        }
      });
    return () => {
      active = false;
    };
  }, [servicesId, redirectUri, handlers]);

  return auth;
};

const runAppleSignIn = async (auth: AppleIdAuth, handlers: MutableRef<AppleSignInHandlers>) => {
  try {
    const response = await auth.signIn();
    const result = await signInClient.apple(toCredential(response), { baseUrl: handlers.current.baseUrl });
    await handlers.current.onSignedIn(result);
  } catch (error) {
    if (!isAppleCancel(error)) {
      handlers.current.onError?.(signInErrorMessage(error, "provider"));
    }
  }
};

export const AppleSignInButton = ({ servicesId, redirectUri, onSignedIn, onError, baseUrl, class: className }: AppleSignInButtonProps) => {
  const handlers = useRef<AppleSignInHandlers>({ onSignedIn, onError, baseUrl });
  handlers.current = { onSignedIn, onError, baseUrl };
  const auth = useAppleIdAuth(servicesId, redirectUri, handlers);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const handleClick = async () => {
    if (!auth || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    await runAppleSignIn(auth, handlers);
    busyRef.current = false;
    setBusy(false);
  };

  return (
    <button
      type="button"
      class={className ?? DEFAULT_APPLE_BUTTON_CLASS}
      disabled={!auth || busy}
      aria-busy={busy}
      onClick={() => {
        void handleClick();
      }}
    >
      {busy ? <Spinner size={20} /> : <AppleIcon />}
      Continue with Apple
    </button>
  );
};
